import $ from '../core/Dom';
import Viewport from '../core/Viewport';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl';
import verge from 'verge';
import Dispatch from "../core/Dispatch";

import { tweenScrollTop } from "../lib/helpers";

const STICKING_MODE_NONE = 'STICKING_MODE_NONE';
const STICKING_MODE_STICKING = 'STICKING_MODE_STICKING';
const STICKING_MODE_BOTTOM = 'STICKING_MODE_BOTTOM';

export default (el, props = {}) => {
    const BP_STICKY_MAP = 980;
    
    const $el = $(el);
    const $mapContainer = $el.find('[data-map-container]');
    const $mapContainerAnchor = $el.find('[data-map-container-anchor]');
    const $listContainer = $el.find('[data-map-list]');
    const $items = $el.find('[data-map-item]');

    const markers = {};

    let map = null;
    let geolocateControl = null;
    let currentlyActive = null;
    let stickyId = null;
    let mapOffset = 0;
    let listHeight = 0;
    let viewportHeight = 0;
    let mapStickyMode = STICKING_MODE_NONE;

    const isSmall = () => !!($listContainer.offset().top !== $mapContainerAnchor.offset().top);

    const updateSizeValues = () => {
        viewportHeight = verge.viewportH();
        mapOffset = $mapContainerAnchor.offset().top;
        listHeight = $listContainer.height();
        if (mapStickyMode === STICKING_MODE_STICKING) {
            $mapContainer.css({ width: `${$mapContainerAnchor.width()}px` });
        }
    };

    const stickit = () => {
        stickyId = requestAnimationFrame(stickit);
        const scrollTop = verge.scrollY();

        if (mapStickyMode === STICKING_MODE_NONE) {
            if (scrollTop > mapOffset) {
                $mapContainer.css({ position: 'fixed', top: 0, bottom: '', width: `${$mapContainerAnchor.width()}px` });
                mapStickyMode = STICKING_MODE_STICKING;
            }
        } else if (mapStickyMode === STICKING_MODE_STICKING) {
            if (scrollTop < mapOffset) {
                $mapContainer.css({ position: '', top: '', bottom: '', width: '' });
                mapStickyMode = STICKING_MODE_NONE;
            }

            if (scrollTop > mapOffset + (listHeight - viewportHeight)) {
                $mapContainer.css({ position: 'absolute', bottom: 0, top: '', width: '' });
                mapStickyMode = STICKING_MODE_BOTTOM;
            }
        } else if (mapStickyMode === STICKING_MODE_BOTTOM) {
            if (scrollTop < mapOffset + (listHeight - viewportHeight)) {
                $mapContainer.css({ position: 'fixed', top: 0, bottom: '', width: `${$mapContainerAnchor.width()}px` });
                mapStickyMode = STICKING_MODE_STICKING;
            }
        }
    };

    const initSticky = () => {
        updateSizeValues();
        stickyId = requestAnimationFrame(stickit);
    };

    const killSticky = () => {
        cancelAnimationFrame(stickyId);
        stickyId = null;
        $mapContainer.css({ position: '', top: '', bottom: '', width: '' });
        mapStickyMode = STICKING_MODE_NONE;
    };

    const deactivateItem = id => {

        (markers[id] || []).forEach(markerInfo => {
            markerInfo.$item.removeClass('-selected');
            if (markerInfo.$marker) {
                markerInfo.$marker.removeClass('-selected');
            }
        });

        updateSizeValues();
    };

    const activateItem = (id, from) => {

        if (currentlyActive !== null) {
            deactivateItem(currentlyActive);
        }

        if (id !== currentlyActive) {

            let centerLatLng = null;

            (markers[id] || []).forEach(markerInfo => {
                markerInfo.$item.addClass('-selected');
                if (markerInfo.$marker) {
                    markerInfo.$marker.addClass('-selected');
                    centerLatLng = centerLatLng || markerInfo;
                }
            });

            if (from === 'list') {
                // TODO if more than one marker, should calculate the "center" of the markers and pan to that. But, just panning to the first valid ltlng, for now - mmikkel
                if (!centerLatLng) {
                    // Pan to start lat/lng
                    const { startLat, startLng } = props;
                    centerLatLng = { lat: startLat || 60.222461, lng: startLng || 10.370213 };
                }
                requestAnimationFrame(() => {
                    map.panTo([centerLatLng.lng, centerLatLng.lat]);
                });
            } else if (isSmall() || listHeight > viewportHeight) {
                // Scroll to the active list item
                requestAnimationFrame(() => {
                    const { $item } = (markers[id] || [])[0] || null;
                    if ($item && $item.length) {
                        let offset = $item.offset().top;
                        if (isSmall()) {
                            offset -= 100;
                        } else {
                            const maxOffset = mapOffset + (listHeight - viewportHeight); // Don't scroll past the sticky mode
                            if (offset > maxOffset) {
                                offset = maxOffset;
                            }
                        }
                        tweenScrollTop(offset, 1, 'Cubic.easeInOut');
                    }
                });
            }

            currentlyActive = id;

        } else if (from === 'list') {
            currentlyActive = null;
        }

        map.resize();
        updateSizeValues();
    };

    const addMarkers = () => {

        const selectedId = parseInt(props.selectedId, 10);

        $items.each(item => {

            const $item = $(item);
            const id = parseInt($item.data('item-id'), 10);
            const isSelected = id === selectedId;
            const itemMarkers = ($item.data('markers') || []).filter(itemMarker => itemMarker && itemMarker.lat && itemMarker.lng);

            if (!itemMarkers.length) {
                markers[id] = [{ id, $item }];
                return;
            }

            itemMarkers.forEach(({ lat, lng }) => {

                const $marker = $(`<div class="interactive-map__marker${isSelected ? ' -selected' : ''}" />`);

                $marker.on('click', () => {
                    activateItem(id, 'marker');
                });

                const marker = new mapboxgl.Marker($marker.get(0))
                    .setLngLat([lng, lat])
                    .addTo(map);

                markers[id] = (markers[id] || []).concat([{ id, $marker, mapboxMarker: marker, $item, lat, lng }]);

            });
        });
    };


    const onResize = () => {
        updateSizeValues();
    };
    
    const onBreakpoint = data => {
        if (data.detail.old.size < BP_STICKY_MAP && data.detail.current.size >= BP_STICKY_MAP) {
            initSticky();
        }
        if (data.detail.old.size >= BP_STICKY_MAP && data.detail.current.size < BP_STICKY_MAP) {
            killSticky();
        }
    };

    const onItemLinkClick = e => {
        e.preventDefault();
        const $trigger = $(e.triggerTarget);
        const $item = $trigger.parent('[data-map-item]');
        const id = $item.data('item-id');
        activateItem(id, 'list');
    };
    
    const initMap = () => {
        mapboxgl.accessToken = 'pk.eyJ1IjoidmFlcnNhYWdvZCIsImEiOiJXeEZ4Z2g4In0.r35861_EPsHP2hB2Ig3PAw';

        map = new mapboxgl.Map({
            container: $mapContainer.get(0),
            style: 'mapbox://styles/vaersaagod/cjw293thb115j1cqoy66syiwc',
            center: [props.startLng ? props.startLng : 10.370213, props.startLat ? props.startLat : 60.222461],
            zoom: props.startZoom ? props.startZoom : 15.0
        });

        // Disable map zoom when using scroll
        map.scrollZoom.disable();

        // Add geolocate control to the map.
        geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true
            },
            trackUserLocation: true
        });
        
        map.addControl(geolocateControl);
        
        // Adds scale
        map.addControl(new mapboxgl.ScaleControl({
            maxWidth: 80,
            unit: 'metric'
        }));
        
        // Add navigation control
        map.addControl(new mapboxgl.NavigationControl());
        
        // Map load event
        map.on('load', () => {
            addMarkers();
        });
    };
    
    const init = () => {
        initMap();
        
        if (Viewport.breakpoint.size >= BP_STICKY_MAP) {
            initSticky();
        }

        if (props.selectedId !== undefined) {
            currentlyActive = props.selectedId;
        }
        
        $el.on('click', '[data-item-link]', onItemLinkClick);

        Viewport.on('breakpoint', onBreakpoint);
        Viewport.on('resize', onResize);
    };

    const destroy = () => {
        killSticky();
        $el.off('click', onItemLinkClick);
        Viewport.off('breakpoint', onBreakpoint);
        Viewport.off('resize', onResize);
    };

    return {
        init,
        destroy
    };
};
